import { useState } from 'react'
import { useLocation } from 'wouter'
import { ChevronLeft, Loader2, Trash2, Wallet, Copy } from 'lucide-react'
import { useAccount } from 'wagmi'
import { Button } from '@/components/ui/button'
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogCancel,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { useUser } from '@/hooks/use-user'
import { useLinkedWallets } from '@/hooks/use-linked-wallets'

const shortenAddress = (addr?: string | null) => {
	if (!addr) return ''
	return `${addr.slice(0, 6)}...${addr.slice(-4)}`
}

const sameAddress = (a?: string | null, b?: string | null) =>
	!!a && !!b && a.toLowerCase() === b.toLowerCase()

export default function LinkedWalletsPage() {
	const [, navigate] = useLocation()
	const { toast } = useToast()
	const { connectWallet, isConnectingWallet } = useUser()
	const { address: connectedAddress, connector, isConnected } = useAccount()
	const {
		linkedWallets,
		isLoading,
		linkWallet,
		removeLinkedWallet,
		isAddressLinked,
	} = useLinkedWallets()

	const [isLinking, setIsLinking] = useState(false)
	const [pendingUnlink, setPendingUnlink] = useState<`0x${string}` | null>(null)
	const [isUnlinking, setIsUnlinking] = useState(false)

	const connectedIsLinked =
		isConnected && connectedAddress
			? isAddressLinked(connectedAddress)
			: false

	const handleLink = async () => {
		if (!isConnected || !connectedAddress) {
			toast({
				title: 'Wallet Not Connected',
				description: 'Connect a wallet first, then link it to your account.',
				variant: 'destructive',
			})
			return
		}
		setIsLinking(true)
		try {
			await linkWallet(connectedAddress as `0x${string}`)
			toast({
				title: 'Wallet Linked',
				description: `${shortenAddress(connectedAddress)} is now linked to your account.`,
			})
		} catch (err: any) {
			toast({
				title: 'Link Failed',
				description: err?.message || 'Failed to link wallet.',
				variant: 'destructive',
			})
		} finally {
			setIsLinking(false)
		}
	}

	const handleConfirmUnlink = async () => {
		if (!pendingUnlink) return
		setIsUnlinking(true)
		try {
			await removeLinkedWallet(pendingUnlink)
			toast({
				title: 'Wallet Unlinked',
				description: `${shortenAddress(pendingUnlink)} has been removed from your account.`,
			})
			setPendingUnlink(null)
		} catch (err: any) {
			toast({
				title: 'Unlink Failed',
				description: err?.message || 'Failed to unlink wallet.',
				variant: 'destructive',
			})
		} finally {
			setIsUnlinking(false)
		}
	}

	const copyToClipboard = async (address: string) => {
		try {
			await navigator.clipboard.writeText(address)
			toast({
				title: 'Address Copied',
				description: 'Address has been copied to clipboard',
			})
		} catch {
			toast({
				title: 'Error',
				description: 'Failed to copy address',
				variant: 'destructive',
			})
		}
	}

	return (
		<div className="h-[100dvh] bg-[#18181B] text-white flex flex-col">
			<div className="max-w-5xl w-full mx-auto flex flex-col flex-1">
				<div
					role="banner"
					className="sticky top-0 z-50 bg-[#18181B] flex items-center justify-start gap-2 text-white py-3 px-5"
				>
					<button
						type="button"
						aria-label="Go back"
						onClick={() => navigate('/account')}
						className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
					</button>
					<h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">
						Linked Wallets
					</h1>
				</div>

				<div className="flex-1 flex items-start justify-center px-5 pb-20 pt-4">
					<div className="w-full max-w-sm">
						<div className="bg-[#212124] rounded-3xl py-6 px-4">
							<p className="text-[#BABABB] text-xs leading-4 font-medium font-figtree mb-4">
								Link external wallets (e.g. MetaMask, WalletConnect) to send
								SECH from them. Linking requires a one-time signature to prove
								ownership.
							</p>

							{/* Connect / Link section */}
							<div className="bg-[#2F2F32] rounded-2xl p-4 mb-4">
								<div className="flex items-center gap-3 mb-3">
									<div className="h-9 w-9 rounded-full bg-[#18181B] flex items-center justify-center">
										<Wallet className="h-5 w-5 text-white" />
									</div>
									<div className="flex flex-col">
										<span className="text-sm leading-4 font-semibold font-figtree text-white">
											{isConnected
												? connector?.name || 'External Wallet'
												: 'No Wallet Connected'}
										</span>
										{isConnected && connectedAddress && (
											<span className="text-xs leading-4 font-medium font-figtree text-[#BABABB]">
												{shortenAddress(connectedAddress)}
											</span>
										)}
									</div>
								</div>

								{!isConnected ? (
									<Button
										onClick={connectWallet}
										disabled={isConnectingWallet}
										className="bg-[#EF4444] h-[42px] rounded-[21px] w-full text-sm leading-4 font-semibold font-figtree"
									>
										{isConnectingWallet ? (
											<>
												<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												Connecting...
											</>
										) : (
											'Connect Wallet'
										)}
									</Button>
								) : connectedIsLinked ? (
									<div className="text-xs leading-4 font-semibold font-figtree text-[#10B981] text-center py-2">
										✓ This wallet is linked to your account
									</div>
								) : (
									<Button
										onClick={handleLink}
										disabled={isLinking}
										className="bg-[#EF4444] h-[42px] rounded-[21px] w-full text-sm leading-4 font-semibold font-figtree"
									>
										{isLinking ? (
											<>
												<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												Awaiting Signature...
											</>
										) : (
											'Link This Wallet'
										)}
									</Button>
								)}
							</div>

							{/* Linked wallets list */}
							<div>
								<h2 className="text-sm leading-4 font-semibold font-figtree text-white mb-3">
									Your Linked Wallets
								</h2>

								{isLoading ? (
									<div className="flex items-center justify-center py-8">
										<Loader2 className="h-5 w-5 animate-spin text-[#EF4444]" />
									</div>
								) : linkedWallets.length === 0 ? (
									<p className="text-xs leading-4 font-medium font-figtree text-[#BABABB] py-2">
										No wallets linked yet. Connect a wallet above and link it
										to send SECH from it.
									</p>
								) : (
									<ul className="space-y-2">
										{linkedWallets.map((wallet) => {
											const isActive = sameAddress(wallet.address, connectedAddress)
											return (
												<li
													key={wallet.address}
													className="bg-[#2F2F32] rounded-xl px-3 py-3 flex items-center justify-between gap-2"
												>
													<div className="flex flex-col gap-1 min-w-0">
														<div className="flex items-center gap-2">
															<span className="text-sm leading-4 font-semibold font-figtree text-white truncate">
																{shortenAddress(wallet.address)}
															</span>
															{isActive && (
																<span className="text-[10px] leading-[10px] font-bold font-figtree text-[#10B981] bg-[#10B981]/10 rounded-full px-2 py-[2px]">
																	ACTIVE
																</span>
															)}
														</div>
														<span className="text-xs leading-4 font-medium font-figtree text-[#BABABB]">
															Linked{' '}
															{new Date(wallet.verifiedAt).toLocaleDateString()}
														</span>
													</div>
													<div className="flex items-center gap-1">
														<button
															type="button"
															onClick={() => copyToClipboard(wallet.address)}
															className="h-9 w-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20"
															aria-label="Copy address"
														>
															<Copy className="h-4 w-4 text-white" />
														</button>
														<button
															type="button"
															onClick={() => setPendingUnlink(wallet.address)}
															className="h-9 w-9 flex items-center justify-center rounded-full bg-[#EF4444]/10 hover:bg-[#EF4444]/20"
															aria-label="Unlink wallet"
														>
															<Trash2 className="h-4 w-4 text-[#EF4444]" />
														</button>
													</div>
												</li>
											)
										})}
									</ul>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>

			<AlertDialog
				open={pendingUnlink !== null}
				onOpenChange={(open) => {
					if (!open && !isUnlinking) setPendingUnlink(null)
				}}
			>
				<AlertDialogContent className="rounded-2xl shadow-lg border fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 border-[#2F2F32] p-6 bg-[#18181B] w-[90%] sm:w-full max-w-sm mx-auto">
					<AlertDialogHeader className="space-y-2 text-center sm:text-center">
						<AlertDialogTitle className="text-lg font-semibold text-white font-figtree text-center">
							Unlink this wallet?
						</AlertDialogTitle>
						<AlertDialogDescription className="text-sm text-[#BABABB] font-figtree text-center">
							{pendingUnlink && shortenAddress(pendingUnlink)} will no longer be
							able to send SECH from this account until it is linked again.
						</AlertDialogDescription>
					</AlertDialogHeader>

					<AlertDialogFooter className="flex flex-col gap-3 sm:flex-col sm:justify-center sm:space-x-0 items-center">
						<Button
							onClick={handleConfirmUnlink}
							disabled={isUnlinking}
							className="w-full h-[42px] bg-[#EF4444] font-figtree text-sm leading-[14px] font-semibold rounded-[26px] text-white shadow-[0_6px_20px_rgba(239,68,68,0.45)] active:scale-[0.96] hover:bg-[#dc2626]"
						>
							{isUnlinking ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Unlinking...
								</>
							) : (
								'Unlink Wallet'
							)}
						</Button>
						<AlertDialogCancel
							onClick={() => setPendingUnlink(null)}
							disabled={isUnlinking}
							className="w-full h-[42px] !mt-0 text-sm leading-[14px] font-semibold font-figtree rounded-[26px] bg-white text-[#18181B] hover:bg-gray-200"
						>
							Cancel
						</AlertDialogCancel>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}
